import React, { useState, useRef, useEffect } from 'react'
import { Radio } from '@/components/atoms'
import { Icon } from '@/icons'

interface RecordingGradingContentsProps {
  word?: string
  recordingUrl?: string
  initialGradingStatus?: 'correct' | 'incorrect' | 'grading'
  onGradingChange?: (status: 'correct' | 'incorrect') => void
  readOnly?: boolean
}

const RecordingGradingContents = ({ 
  word = '단어', 
  recordingUrl, 
  initialGradingStatus = 'grading',
  onGradingChange,
  readOnly = false
}: RecordingGradingContentsProps) => {
  const [gradingStatus, setGradingStatus] = useState<'correct' | 'incorrect'>(
    initialGradingStatus === 'grading' ? 'correct' : initialGradingStatus
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // initialGradingStatus가 변경될 때 상태 업데이트
  useEffect(() => {
    setGradingStatus(initialGradingStatus === 'grading' ? 'correct' : initialGradingStatus);
  }, [initialGradingStatus]);

  useEffect(() => {
    if (recordingUrl && audioRef.current) {
      audioRef.current.src = recordingUrl;
    }
  }, [recordingUrl]);

  const handlePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(Math.floor(audioRef.current.duration));
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(Math.floor(audioRef.current.currentTime));
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleGradingChange = (status: 'correct' | 'incorrect') => {
    setGradingStatus(status);
    if (onGradingChange) {
      onGradingChange(status);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className='p-5'>
        <h1 className='heading1'>{readOnly ? '녹음 듣기' : '녹음 채점하기'}</h1>
        
        {/* 단어 표시 */}
        <div className='my-4 text-center'>
          <p className='text-2xl font-bold text-center text-gray-800'>{word}</p>
        </div>

        {/* 오디오 재생 영역 */}
        <div className='my-6'>
          <audio
            ref={audioRef}
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
          />
          
          <div className='flex flex-col gap-3 items-center p-6 bg-gray-50 rounded-2xl'>
            {recordingUrl ? (
              <>
                {/* 재생 버튼 */}
                <button
                  onClick={handlePlay}
                  className='flex justify-center items-center w-16 h-16 bg-blue-500 rounded-full transition-colors hover:bg-blue-600'
                >
                  {isPlaying ? (
                    <Icon name="pause" size={32} color="white" />
                  ) : (
                    <Icon name="play" size={32} color="white" />
                  )}
                </button>

                {/* 재생 시간 */}
                <div className='text-sm text-gray-600'>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>

                {/* 진행 바 */}
                <div className='overflow-hidden w-full h-2 bg-gray-200 rounded-full'>
                  <div 
                    className='h-full bg-blue-500 transition-all duration-200'
                    style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
                  />
                </div>
              </>
            ) : (
              <p className='text-sm text-gray-500'>녹음된 내용이 없습니다.</p>
            )}
          </div>
        </div>

        {/* 채점 라디오 버튼 (읽기 전용일 때 숨김) */}
        {!readOnly && (
          <div className='flex gap-12 justify-center max-w-[300px] mx-auto'>
              <Radio 
                label='정답' 
                name='grading' 
                onChange={() => handleGradingChange('correct')} 
                checked={gradingStatus === 'correct'} 
              />
              <Radio 
                label='오답' 
                name='grading' 
                onChange={() => handleGradingChange('incorrect')} 
                checked={gradingStatus === 'incorrect'} 
              />
          </div>
        )}
    </div>
  )
}

export default RecordingGradingContents